<template>
  <div>
    <base-modal
      :showing="visible && !deleteVisible && !detailMovement.visible"
      :size="size"
      @close="onClose"
      @opened="onOpened"
    >
      <base-card
        :title="`Detail ${label}`"
        :description="`Detail Faktur ${label}`"
        :with-style="false"
      >
        <template #action>
          <base-badge
            :color="
              order.data.attributes.is_valid_for_payment ? 'green' : 'yellow'
            "
            >{{ order.data.attributes.order_status }}</base-badge
          >
        </template>

        <div class="space-y-6 border-t border-gray-200 py-5">
          <dl class="grid gap-6 sm:grid-cols-3">
            <slot
              name="attributes"
              :classes="{
                dt: 'text-xs text-gray-700',
                dd: 'mt-1 text-sm font-bold text-gray-900',
              }"
              :order="order"
              :destination-office="destinationOffice"
              :save-reference-code="onSaveReferenceCode"
            >
              <div v-if="columns.includes('reference_code')" class="w-full">
                <dt class="text-xs text-gray-700">No {{ label }}</dt>
                <dd class="mt-1 text-sm font-bold text-gray-900">
                  <base-input
                    size="sm"
                    placeholder="Masukkan no order"
                    :shadow="false"
                    type="text"
                    v-model="order.data.attributes.reference_code"
                    @change="onSaveReferenceCode"
                  />
                </dd>
              </div>
              <div v-if="columns.includes('code')" class="w-full">
                <dt class="text-xs text-gray-700">No {{ label }}</dt>
                <dd class="mt-1 text-sm text-gray-700">
                  <OrderCodeLabel :order="order.data" />
                </dd>
              </div>
              <div v-if="columns.includes('warehouse')" class="w-full">
                <dt class="text-xs text-gray-700">Kode Gudang</dt>
                <dd class="mt-1 text-sm font-bold text-gray-900">
                  {{ order.data.attributes.origin_office_code }} /
                  {{ order.data.attributes.origin_warehouse_code }}
                </dd>
              </div>
              <div v-if="columns.includes('office')" class="w-full">
                <dt class="text-xs text-gray-700">Kode / Nama Kantor</dt>
                <dd class="mt-1 text-sm font-bold text-gray-900">
                  {{
                    destinationOffice ? destinationOffice.attributes.code : '-'
                  }}
                  /
                  {{
                    destinationOffice ? destinationOffice.attributes.name : '-'
                  }}
                </dd>
              </div>
              <div v-if="columns.includes('date')" class="w-full">
                <dt class="text-xs text-gray-700">Dibuat Tanggal</dt>
                <dd class="mt-1 text-sm font-bold text-gray-900">
                  <order-date-label :order="order.data" />
                </dd>
              </div>
              <div v-if="columns.includes('user')" class="w-full">
                <dt class="text-xs text-gray-700">Dibuat Oleh</dt>
                <dd class="mt-1 text-sm font-bold text-gray-900">
                  {{
                    order.data.attributes.created_by
                      ? order.data.attributes.created_by.office.name
                      : '-'
                  }}
                  /
                  {{
                    order.data.attributes.created_by
                      ? order.data.attributes.created_by.user.name
                      : '-'
                  }}
                </dd>
              </div>
              <div v-if="columns.includes('area')" class="w-full">
                <dt class="text-xs text-gray-700">Wilayah Area</dt>
                <dd class="mt-1 text-sm font-bold text-gray-900">
                  <div class="flex items-center gap-2">
                    <p>
                      {{ order.data.attributes.area_name }}
                      ({{ order.data.attributes.area_code }})
                    </p>
                    <order-area-status-badge
                      v-if="
                        order.data.attributes.area_status &&
                        order.data.attributes.area_status !== 'valid'
                      "
                      :area-status="order.data.attributes.area_status"
                    />
                  </div>
                </dd>
              </div>
              <div v-if="columns.includes('shipment_type')" class="w-full">
                <dt class="text-xs text-gray-700">Diambil / Dikirim</dt>
                <dd class="mt-1 text-sm font-bold text-gray-900">
                  {{
                    order.data.attributes.order_shipment === 'pickup'
                      ? 'Diambil'
                      : 'Dikirim'
                  }}
                </dd>
              </div>
              <div v-if="columns.includes('stock_type')" class="w-full">
                <dt class="text-xs text-gray-700">Metode {{ label }}</dt>
                <dd class="mt-1 text-sm font-bold text-gray-900">
                  {{
                    order.data.attributes.is_using_stock
                      ? 'Dengan Barang'
                      : 'Tanpa Barang'
                  }}
                </dd>
              </div>
              <div v-if="columns.includes('loan')" class="w-full">
                <template v-if="order.data.attributes.is_using_stock">
                  <dt class="text-xs text-gray-700">Metode Pembayaran</dt>
                  <dd class="mt-1 text-sm font-bold text-gray-900">
                    {{
                      order.data.attributes.is_loan ? 'Pinjam Barang' : 'Saldo'
                    }}
                  </dd>
                </template>
              </div>
              <div
                v-if="
                  columns.includes('address') &&
                  order.data.attributes.order_shipment === 'delivery'
                "
                class="w-full"
              >
                <dt class="text-xs text-gray-700">Dikirim ke Alamat</dt>
                <dd class="mt-1 text-sm font-bold text-gray-900">
                  {{ order.data.attributes.complete_shipment_address || '-' }}
                </dd>
              </div>
              <div class="w-full">
                <dt class="text-xs text-gray-700">Catatan</dt>
                <dd class="mt-1 text-sm font-bold text-gray-900">
                  {{ order.data.attributes.notes || '-' }}
                </dd>
              </div>
            </slot>
            <div v-if="withItems && orderType === 'order' && hasItems" class="col-span-full">
              <OrderDetailTable
                :order-id="orderId"
                :order="order"
                :with-payment="withPayment"
                :with-shipping-fee="withShippingFee"
                :columns="orderDetailColumns"
                :display-default-area-price="displayDefaultAreaPrice"
                @update-loading="(value) => (loadingOrderDetails = value)"
                @load="({ hasItems: value }) => hasItems = value"
              />
            </div>
            <div v-if="(withBonus || orderType === 'reimbursement' || orderType === 'free_reimbursement') && hasBonus" class="col-span-full">
              <OrderDetailBonusTable
                :preview="previewBonus"
                :order-id="orderId"
                :columns="orderDetailBonusColumns || [...orderDetailColumns, 'free_follow_up_qty', 'regular_stock_qty']"
                :type="orderType"
                @update-loading="(value) => (loadingOrderDetailBonuses = value)"
                @load="({ hasItems: value }) => hasBonus = value"
              />
            </div>
            <div v-if="withGroup && hasGroup" class="col-span-full">
              <OrderGroupTable
                :order-id="orderId"
                @load="({ hasItems: value }) => hasGroup = value"
              />
            </div>
            <div
              v-if="withPayment && order.data.attributes.is_using_stock"
              class="col-span-full"
            >
              <dt class="text-xs text-gray-700">Pembayaran</dt>
              <dd class="mt-1">
                <payment-list
                  :order-code="`${order.data.attributes.origin_code}/${order.data.attributes.destination_code}`"
                  :order-valid="order.data.attributes.is_valid_for_payment"
                  :summary="false"
                  :columns="[]"
                  :payments="{ data: payments }"
                  :read-only="!order.data.attributes.is_valid_for_payment"
                  view-as="order"
                  cardless
                />
              </dd>
            </div>
            <template v-if="withShipment && order.data.attributes.is_using_stock">
              <div
                v-if="stockMovements.length"
                class="col-span-full"
              >
                <dt class="text-xs text-gray-700">Pengiriman</dt>
                <dd class="mt-1">
                  <datatable
                    :scroll-x="false"
                    :columns="[
                      { id: 'code', name: 'Kode Kemasan' },
                      { id: 'date', name: 'Tanggal' },
                      { id: 'status', name: 'Status', theadClass: 'text-center' },
                    ]"
                  >
                    <template #tbody="{ classes }">
                      <tr
                        v-for="(stockMovement, index) in stockMovements"
                        :key="index"
                        :class="[classes.tr, 'cursor-pointer hover:bg-green-100']"
                        @click="onDetailMovement(stockMovement)"
                      >
                        <td :class="[classes.td]">
                          <p class="font-bold text-gray-900">
                            {{ stockMovement.attributes.code }}
                          </p>
                        </td>
                        <td :class="[classes.td]">
                          {{ stockMovement.attributes.createdAt | formatDate }}
                        </td>
                        <td :class="[classes.td, 'text-center']">
                          <base-badge
                            :color="getShipmentStatusColor(stockMovement)"
                            >{{
                              getShipmentStatusText(stockMovement)
                            }}</base-badge
                          >
                        </td>
                      </tr>
                    </template>
                  </datatable>
                </dd>
              </div>
              <div
                v-if="withIndent && indentStockMovements.length"
                class="col-span-full"
              >
                <dt class="text-xs text-gray-700">Penerimaan</dt>
                <dd class="mt-1">
                  <datatable
                    :scroll-x="false"
                    :columns="[
                      { id: 'code', name: 'Kode Kemasan' },
                      { id: 'type', name: 'Tipe' },
                      { id: 'date', name: 'Tanggal' },
                      { id: 'status', name: 'Status', theadClass: 'text-center' },
                    ]"
                  >
                    <template #tbody="{ classes }">
                      <tr
                        v-for="(stockMovement, index) in indentStockMovements"
                        :key="index"
                        :class="[classes.tr, 'cursor-pointer hover:bg-green-100']"
                        @click="onDetailMovement(stockMovement)"
                      >
                        <td :class="[classes.td]">
                          <p class="font-bold text-gray-900">
                            {{ stockMovement.attributes.code }}
                          </p>
                        </td>
                        <td :class="[classes.td]">
                          {{ stockMovement.attributes.for_reimbursement ? 'Reimbursement' : 'Susulan Promo' }}
                        </td>
                        <td :class="[classes.td]">
                          {{ stockMovement.attributes.createdAt | formatDate }}
                        </td>
                        <td :class="[classes.td, 'text-center']">
                          <base-badge
                            :color="getShipmentStatusColor(stockMovement)"
                            >{{
                              getShipmentStatusText(stockMovement)
                            }}</base-badge
                          >
                        </td>
                      </tr>
                    </template>
                  </datatable>
                </dd>
              </div>
            </template>
            <template v-if="withFollowUp && stockMovements.length">
              <div v-if="withItems && hasFollowUpItems" class="col-span-full">
                <OrderDetailTable
                  type="follow-up"
                  :order-id="orderId"
                  :order="order"
                  :with-payment="false"
                  :with-shipping-fee="false"
                  :columns="['code', 'qty']"
                  @update-loading="(value) => (loadingOrderDetails = value)"
                  @load="({ hasItems: value }) => hasFollowUpItems = value"
                />
              </div>
              <div v-if="withBonus && hasFollowUpBonus" class="col-span-full">
                <OrderDetailBonusTable
                  type="follow-up"
                  :order-id="orderId"
                  @update-loading="(value) => (loadingOrderDetailBonuses = value)"
                  @load="({ hasItems: value }) => hasFollowUpBonus = value"
                />
              </div>
            </template>
          </dl>
        </div>
        <div class="flex flex-wrap gap-2 sm:justify-end">
          <slot name="actions" :order="order" :stock-movements="stockMovements" />

          <base-button v-if="orderType === 'order' && withLog" color="white" @click="orderLogModalVisible = true">
            <Icon icon="heroicons:clock" />
            Cek Faktur
          </base-button>

          <base-button
            v-if="
              !order.data.attributes.deletedAt &&
              order.data.attributes.previous_order_id
            "
            color="white"
            @click="prevOrderModalVisible = true"
          >
            <Icon icon="heroicons:arrow-top-right-on-square" />
            Faktur Awal
          </base-button>

          <base-button v-if="printable" color="white" @click="onPrint">
            <Icon icon="heroicons:printer" />
            Print
          </base-button>

          <template
            v-if="
              !readonly &&
              !order.data.attributes.is_valid_for_payment &&
              !order.data.attributes.deletedAt
            "
          >
            <base-button :disabled="!asCreator" :title="!asCreator ? 'Dibuat oleh kantor lain' : ''" color="white" @click="onDelete">
              <Icon icon="heroicons:trash" />
              {{
                order.data.attributes.is_cancel
                  ? 'Batal Faktur Permanen'
                  : 'Hapus'
              }}
            </base-button>
            <base-button
              v-if="order.data.attributes.is_cancel"
              :disabled="!asCreator"
              :title="!asCreator ? 'Dibuat oleh kantor lain' : ''"
              @click="onReissue"
              color="white"
            >
              <Icon icon="heroicons:pencil-square" />
              Faktur Ulang
            </base-button>
            <template v-if="order.data.id && !order.data.attributes.is_cancel">
              <base-button
                v-if="!asCreator"
                disabled
                title="Dibuat oleh kantor lain"
                color="white"
              >
                <Icon icon="heroicons:pencil-square" />
                Edit
              </base-button>
              <base-button
                v-else
                :to="{
                  name: editRouteName,
                  params: {
                    id: order.data.id,
                  },
                }"
                color="white"
              >
                <Icon icon="heroicons:pencil-square" />
                Edit
              </base-button>
            </template>
            <base-button
              v-if="!order.data.attributes.is_cancel"
              :disabled="!asCreator"
              :title="!asCreator ? 'Dibuat oleh kantor lain' : ''"
              @click="onValidate"
            >
              <Icon icon="heroicons:shield-check" />
              Validasi
            </base-button>
          </template>
        </div>
      </base-card>
    </base-modal>

    <base-confirm
      v-if="deleteVisible"
      @close="deleteVisible = false"
      @confirmed="onDeleteConfirmed"
      size="max-w-xl"
      :icon="false"
      message="Ketik ulang kalimat berikut ini untuk mengkonfirmasi aksi pembatalan"
      :code="`Faktur dengan kode ${order.data.attributes.origin_code} akan saya hapus`"
      :center="false"
      input
      visible
    />

    <reissue-order-confirmation
      :order-id="reissueConfirmation.orderId"
      :visible="reissueConfirmation.visible"
      @close="reissueConfirmation.visible = false"
      @confirmed="onReissued"
    />

    <view-stock-movement-modal
      v-bind="stockMovementModalProps"
      readonly
      :visible="detailMovement.visible"
      :stock-movement-id="detailMovement.stockMovementId"
      @close="detailMovement.visible = false"
    />

    <view-order-modal
      label="Faktur Awal"
      :with-items="false"
      :with-shipment="false"
      :with-payment="false"
      v-if="order.data.attributes.previous_order_id"
      :order-id="order.data.attributes.previous_order_id"
      :visible="prevOrderModalVisible"
      @close="prevOrderModalVisible = false"
    />

    <view-order-log-modal
      :visible="orderLogModalVisible"
      :order-id="orderId"
      @close="orderLogModalVisible = false"
    />

    <vue-html2pdf
      v-if="printable && destinationOffice"
      :show-layout="false"
      :float-layout="true"
      :preview-modal="true"
      :enable-download="true"
      :paginate-elements-by-height="5000"
      :filename="printFileName"
      :pdf-quality="2"
      :manual-pagination="false"
      pdf-format="a4"
      pdf-orientation="portrait"
      pdf-content-width="210mm"
      ref="html2Pdf"
    >
      <print-order-member-pdf
        v-if="destinationOffice.attributes.office_type === 'member'"
        slot="pdf-content"
        :order="order.data"
        :origin-office="originOffice"
        :destination-office="destinationOffice"
        :order-details="getIncludedByType(order, 'order-details')"
        :order-detail-bonuses="[]"
      />
      <print-order-pdf
        v-else
        slot="pdf-content"
        :order="order.data"
        :destination-office="destinationOffice"
        :order-details="printOrder.orderDetails"
        :order-detail-bonuses="printOrder.orderDetailBonuses"
      />
    </vue-html2pdf>

    <teleport to="body">
      <loading
        v-if="loading || loadingOrderDetails || loadingOrderDetailBonuses"
      />
    </teleport>
  </div>
</template>

<script>
import { requestMixin } from '@/mixins/request/request';
import BaseModal from '@/components/base/BaseModal.vue';
import BaseConfirm from '@/components/base/BaseConfirm.vue';
import Teleport from 'vue2-teleport';
import PaymentList from '@/components/payment/payment-list.vue';
import ViewStockMovementModal from '@/components/stock-movement/view-stock-movement-modal.vue';
import ReissueOrderConfirmation from './reissue-order-confirmation.vue';
import OrderDateLabel from './order-date-label.vue';
import { mapActions, mapGetters } from 'vuex';
import VueHtml2pdf from 'vue-html2pdf';
import PrintOrderPdf from './print-order-pdf.vue';
import PrintOrderMemberPdf from './print-order-member-pdf.vue';
import dayjs from 'dayjs';
import OrderDetailTable from './OrderDetailTable.vue';
import OrderDetailBonusTable from './OrderDetailBonusTable.vue';
import OrderAreaStatusBadge from './order-area-status-badge.vue';
import ViewOrderLogModal from './view-order-log-modal.vue';
import OrderCodeLabel from './order-code-label.vue';
import OrderGroupTable from './OrderGroupTable.vue';

export default {
  name: 'ViewOrderModal',
  mixins: [requestMixin],
  components: {
    BaseModal,
    Teleport,
    PaymentList,
    BaseConfirm,
    ViewStockMovementModal,
    ReissueOrderConfirmation,
    OrderDateLabel,
    VueHtml2pdf,
    PrintOrderPdf,
    PrintOrderMemberPdf,
    OrderDetailTable,
    OrderDetailBonusTable,
    OrderAreaStatusBadge,
    ViewOrderLogModal,
    OrderCodeLabel,
    OrderGroupTable
  },
  props: {
    withLog: {
      type: Boolean,
      default: true
    },
    withFollowUp: {
      type: Boolean,
      default: true
    },
    withGroup: Boolean,
    size: {
      type: String,
      default: 'max-w-5xl',
    },
    orderType: {
      type: String,
      default: 'order'
    },
    printable: Boolean,
    displayDefaultAreaPrice: Boolean,
    stockMovementModalProps: Object,
    visible: Boolean,
    orderId: String,
    type: String,
    editRouteName: {
      type: String,
      default: 'restock-pbs.edit',
    },
    label: {
      type: String,
      default: 'Restock',
    },
    withPayment: {
      type: Boolean,
      default: true,
    },
    withShipment: {
      type: Boolean,
      default: true,
    },
    withItems: {
      type: Boolean,
      default: true,
    },
    columns: {
      type: Array,
      default: () => [
        'code',
        'warehouse',
        'office',
        'date',
        'user',
        'area',
        'shipment_type',
        'stock_type',
        'loan',
        'address',
      ],
    },
    orderDetailColumns: {
      type: Array,
      default: () => ['code', 'qty', 'price', 'total_price'],
    },
    orderDetailBonusColumns: Array,
    readonly: Boolean,
    withBonus: Boolean,
    withShippingFee: Boolean,
    viewAs: String,
    filterShipped: {
      type: Boolean,
      default: true
    },
    preview: Object,
    previewBonus: Object,
    withIndent: Boolean
  },
  emits: ['close', 'validated', 'deleted', 'reissued'],
  data() {
    return {
      deleteVisible: false,
      detailMovement: {
        visible: false,
        stockMovementId: null,
      },
      orderLogModalVisible: false,
      loading: false,
      loadingOrderDetails: false,
      loadingOrderDetailBonuses: false,
      order: {
        data: {
          attributes: {},
          relationships: {
            'origin-office': {
              data: null,
            },
            'destination-office': {
              data: null,
            },
            'order-details': {
              data: [],
            },
            'order-detail-bonuses': {
              data: [],
            },
            payments: {
              data: [],
            },
            'stock-movements': {
              data: [],
            },
          },
        },
        included: [],
      },
      prevOrderModalVisible: false,
      reissueConfirmation: {
        orderId: null,
        visible: false,
      },
      reload: true,
      printOrder: {
        orderDetails: [],
        orderDetailBonuses: []
      },
      hasItems: true,
      hasBonus: true,
      hasFollowUpItems: true,
      hasFollowUpBonus: true,
      hasGroup: true
    }
  },
  computed: {
    ...mapGetters({
      me: 'auth/getUser'
    }),
    asCreator() {
      if (!this.type) {
        return true;
      }

      if (
        this.order.data.attributes.order_type === 'purchase' &&
        this.type === 'purchase'
      ) {
        return true;
      }

      if (
        this.order.data.attributes.order_type === 'sale' &&
        this.type === 'sale'
      ) {
        if (!this.originOffice) {
          return true
        }

        return this.originOffice.id === this.me.office_id;
      }

      return false;
    },
    originOffice() {
      if (!this.order.data.relationships['origin-office'].data) {
        return null;
      }

      return this.getSingleIncluded(
        this.order,
        this.order.data.relationships['origin-office'].data.id
      );
    },
    destinationOffice() {
      if (!this.order.data.relationships['destination-office'].data) {
        return null;
      }

      return this.getSingleIncluded(
        this.order,
        this.order.data.relationships['destination-office'].data.id
      );
    },
    printFileName() {
      const datetime = dayjs(this.order.data.attributes.createdAt).format('YYYYMMDD')
      const type = {
        'pusat': 'po',
        'kantor_pelayanan': 'restock_kp',
        'stockist_center': 'restock_sc',
        'stockist': 'faktur'
      }[this.destinationOffice.attributes.office_type]
      const code = `${this.order.data.attributes.origin_code}_${this.order.data.attributes.destination_code}`
      const user = this.me.office_code

      return [datetime, type, code, user].join('-')
    },
    payments() {
      if (!this.order.data.relationships['payments'].data) {
        return null;
      }

      return this.getIncludedByType(this.order, 'payments');
    },
    stockMovements() {
      if (!this.order.data.relationships['stock-movements'].data) {
        return null;
      }

      const filterShipped = stockMovement => this.filterShipped ? stockMovement.attributes.is_shipped : true

      if (this.orderType === 'reimbursement') {
        return this.getIncludedByType(this.order, 'stock-movements')
          .filter(stockMovement => stockMovement.attributes.for_reimbursement)  
          .filter(filterShipped)
      }

      if (this.orderType === 'free_reimbursement') {
        return this.getIncludedByType(this.order, 'stock-movements')
          .filter(stockMovement => stockMovement.attributes.for_free_reimbursement)  
          .filter(filterShipped)
      }

      return this.getIncludedByType(this.order, 'stock-movements')
        .filter(stockMovement => !stockMovement.attributes.for_reimbursement && !stockMovement.attributes.for_free_reimbursement) 
        .filter(filterShipped)
    },
    indentStockMovements() {
      if (!this.order.data.relationships['stock-movements'].data) {
        return null;
      }

      const filterShipped = stockMovement => this.filterShipped ? stockMovement.attributes.is_shipped : true

      return this.getIncludedByType(this.order, 'stock-movements')
        .filter(stockMovement => stockMovement.attributes.for_reimbursement || stockMovement.attributes.for_free_reimbursement) 
        .filter(filterShipped)
    },
  },
  methods: {
    ...mapActions({
      createAlert: 'alert/createAlert',
    }),
    getShipmentStatusColor(stockMovement) {
      if (!stockMovement.attributes.is_valid_for_shipment) {
        return 'yellow';
      }

      if (!stockMovement.attributes.is_shipped) {
        return 'red';
      }

      if (!stockMovement.attributes.is_received) {
        return 'yellow';
      }

      return 'green';
    },
    getShipmentStatusText(stockMovement) {
      if (!stockMovement.attributes.is_valid_for_shipment) {
        return 'Belum Dikemas';
      }

      if (!stockMovement.attributes.is_shipped) {
        return 'Belum Dikirim';
      }

      if (!stockMovement.attributes.is_received) {
        return 'Belum Diterima';
      }

      return 'Diterima';
    },
    async loadOrder() {
      if (this.preview) {
        this.order = this.preview
      } else {
        const [res, err] = await this.request(`/api/v1/orders/${this.orderId}`, {
          params: {
            include: `destination-office,origin-office,payments,stock-movements${
              this.viewAs === 'stockist-cashbill' ? ',order-details' : ''
            }`,
            'fields[orders]': `discount_price,notes,area_status,deletedAt,previous_order_id,origin_code,reference_code,destination_code,is_valid_for_payment,order_status,is_cancel,origin_office_code,origin_warehouse_code,destination_office_code,destination_warehouse_code,createdAt,orderedAt,order_type,created_by,area_name,area_code,order_shipment,is_using_stock,is_loan,shipment_address,grand_total_pv,grand_total_bv,grand_total_price,default_area_price,payment_amount,cashier_payment_amount,destination-office,origin-office,payments,stock-movements,latest_update_by,updatedAt,order-detail-bonuses,shipping_fee,plastic_qty,complete_shipment_address,free_reimbursement_status,reimbursement_status,validated_by${
              this.viewAs === 'stockist-cashbill' ? ',order-details' : ''
            }`,
            'fields[payments]':
              'code,destination_office_code,origin_warehouse_code,createdAt,total_amount,cashier_total_amount,state,is_verified',
            'fields[stock-movements]':
              'code,createdAt,is_shipped,is_received,is_valid_for_shipment,is_packed,for_reimbursement,for_free_reimbursement',
            'fields[order-details]':
              'product_code,product_name,point_value,bonus_value,product_qty,product_price,total_price',
          },
        });

        if (!err) {
          this.hasItems = true
          this.hasBonus = true
          this.hasFollowUpItems = true
          this.hasFollowUpBonus = true
          this.hasGroup = true

          this.order = res;
        }
      }
    },
    onClose() {
      this.reload = true;

      this.$emit('close');
    },
    onDelete() {
      this.reload = false;
      this.deleteVisible = true;
    },
    async onDeleteConfirmed() {
      this.loading = true;

      const [, err] = await this.request(
        `/api/v1/orders/${this.order.data.id}`,
        {
          method: 'patch',
          data: {
            data: {
              type: 'orders',
              id: this.order.data.id,
              attributes: {
                deletedAt: new Date(),
              },
            },
          },
        }
      );

      if (err) {
        this.createAlert({
          data: this.getRequestErrorMessage(err),
          status: 'error',
        });
      } else {
        this.deleteVisible = false;
        this.reload = true;

        this.$emit('deleted');
      }

      this.loading = false;
    },
    async onOpened() {
      if (this.reload) {
        this.loading = true;

        await this.loadOrder();

        this.loading = false;
      }
    },
    async onValidate() {
      this.loading = true;

      const [, err] = await this.request(
        `/api/v1/orders/${this.order.data.id}/-actions/validateOrder`,
        {
          method: 'patch',
        }
      );

      if (err) {
        this.createAlert({
          data: this.getRequestErrorMessage(err),
          status: 'error',
        });
      } else {
        this.reload = true;

        this.$emit('validated');
      }

      this.loading = false;
    },
    async onDetailMovement(movement) {
      this.detailMovement.visible = true;
      this.detailMovement.stockMovementId = movement.id;

      this.reload = false;
    },
    onReissue() {
      this.reissueConfirmation.orderId = this.order.data.id;
      this.reissueConfirmation.visible = true;
    },
    onReissued() {
      this.reissueConfirmation.visible = false;

      this.onClose();

      this.$emit('reissued');
    },
    async onSaveReferenceCode() {
      this.loading = true;

      const [, err] = await this.request(
        `/api/v1/orders/${this.order.data.id}`,
        {
          method: 'patch',
          data: {
            data: {
              type: 'orders',
              id: this.order.data.id,
              attributes: {
                reference_code: this.order.data.attributes.reference_code,
              },
            },
          },
        }
      );

      if (err) {
        this.createAlert({
          data: this.getRequestErrorMessage(err),
          status: 'error',
        });
      }

      this.loading = false;
    },
    async loadPrintOrderDetails(type) {
      const [res, err] = await this.request(
        `/api/v1/orders/${this.order.data.id}/${type}`
      );

      if (!err) {
        return res
      }
      
      return {
        data: []
      }
    },
    async onPrint() {
      this.loading = true

      const [orderDetails, orderDetailBonuses] = await Promise.all([this.loadPrintOrderDetails('order-details'), this.loadPrintOrderDetails('order-detail-bonuses')])

      this.printOrder.orderDetails = orderDetails.data
      this.printOrder.orderDetailBonuses = orderDetailBonuses.data

      this.loading = false

      this.$refs.html2Pdf.generatePdf();
    },
  },
};
</script>
